/// <reference lib="dom"/>
/** @jsx hh */
/** @jsxFrag ChiFragment */
import { ChiFragment, hh } from "@chi/chi";
import { useMemo } from "@chi/chi/hooks";
import { rngSeed } from "@utils/utils";
import type { ParamDict, ParamDictProps, ParamList } from "@utils/params";
import { At, Noise, type WbaxAutomation, type WbaxNoiseType, WbaxNoiseTypes } from "@chi/wbax";
import { ParseNumbers, type SynthDef, SynthOpts, type SynthVoiceProps } from "@/synth/synth.tsx";
import { BiquadTypes, FilterSweep, Ramp, RampTypes } from "@/synth/blocks.tsx";

//#region wind
export const WindSynthParams = {
  "wind/duration": { type: "float", value: 5, min: 0.5, max: 30, step: 0.1 },
  "filter/type": { type: "list", value: "lowpass", list: BiquadTypes } as ParamList<
    typeof BiquadTypes
  >,
  "filter/q": { type: "float", value: 0.7, min: 0.1, max: 10, step: 0.1 },
  "filter/startFreq": { type: "int", value: 400, min: 0, max: 1000 },
  "filter/modulation/min": { type: "int", value: 300, min: 0, max: 1000 },
  "filter/modulation/range": { type: "int", value: 400, min: 0, max: 1000 },
  "gain/start": { type: "float", value: 0.15, min: 0, max: 1, step: 0.01 },
  "gain/modulation/min": { type: "float", value: 0.1, min: 0, max: 1, step: 0.01 },
  "gain/modulation/range": { type: "float", value: 0.2, min: 0, max: 1, step: 0.01 },
  "modulation/points": { type: "float", value: 0.5, min: 0.1, max: 10, step: 0.1 },
  "modulation/ramp": { type: "float", value: 0.5, min: 0.1, max: 10, step: 0.1 },
  "rng/seed": { type: "int", value: 12345, min: 1, max: 999_999_999 },
} satisfies ParamDict;
export type WindSynthOpts = ParamDictProps<typeof WindSynthParams>;

export function WindAutomation(o: WindSynthOpts): { gain: WbaxAutomation; freq: WbaxAutomation } {
  const duration = o["wind/duration"];
  const gain: WbaxAutomation = [{ set: o["gain/start"], at: 0 }];
  const freq: WbaxAutomation = [{ set: o["filter/startFreq"], at: 0 }];
  const points = Math.floor(duration / o["modulation/points"]);
  const rng = rngSeed(0);

  for (let i = 0; i < points - 1; i++) {
    const at = i * o["modulation/ramp"] + o["modulation/ramp"];
    gain.push({ linear: o["gain/modulation/min"] + rng() * o["gain/modulation/range"], at });
    freq.push({ linear: o["filter/modulation/min"] + rng() * o["filter/modulation/range"], at });
  }

  gain.push({ linear: o["gain/start"], at: duration });
  freq.push({ linear: o["filter/startFreq"], at: duration });
  return { gain, freq };
}

export const Wind = (props: Partial<WindSynthOpts> & SynthVoiceProps & { loop?: boolean }) => {
  const o = SynthOpts(WindSynth, props);
  const key = JSON.stringify(o);
  const rng = useMemo(() => rngSeed(o["rng/seed"]), [o["rng/seed"]]);
  const { gain, freq } = useMemo(() => WindAutomation(o), [key]);
  return (
    <gain gain={gain}>
      <biquad type={o["filter/type"]} Q={o["filter/q"]} frequency={freq}>
        <Noise duration={o["wind/duration"]} rng={rng} loop={props.loop} onEnded={props.onEnded} />
      </biquad>
    </gain>
  );
};

export const WindSynth: SynthDef<typeof WindSynthParams> = {
  label: "wind",
  params: WindSynthParams,
  Component: Wind,
  duration: (o) => o["wind/duration"],
};
//#endregion

//#region shush
export const ShushSynthParams = {
  "filter/type": { type: "list", value: "bandpass", list: BiquadTypes } as ParamList<
    typeof BiquadTypes
  >,
  "filter/frequency": { type: "int", value: 1300, min: 0, max: 2000 },
  "filter/ramp": { type: "int", value: 600, min: 0, max: 1000 },
  "filter/q": { type: "float", value: 0.7, min: 0.1, max: 10, step: 0.1 },
  "filter/duration": { type: "float", value: 0.7, min: 0.1, max: 10, step: 0.1 },
  "lowpass/type": { type: "list", value: "lowpass", list: BiquadTypes } as ParamList<
    typeof BiquadTypes
  >,
  "lowpass/frequency": { type: "int", value: 1500, min: 0, max: 2000 },
  "lowpass/ramp": { type: "int", value: 800, min: 0, max: 2000 },
  "lowpass/q": { type: "float", value: 0.5, min: 0.1, max: 10, step: 0.1 },
  "lowpass/duration": { type: "float", value: 0.7, min: 0.1, max: 10, step: 0.1 },
  "envelope/attack/ramp": { type: "list", value: "linear", list: RampTypes } as ParamList<
    typeof RampTypes
  >,
  "envelope/attack/value": { type: "float", value: 0.5, min: 0, max: 1, step: 0.01 },
  "envelope/attack/t": { type: "float", value: 0.3, min: 0.1, max: 10, step: 0.1 },
  "envelope/decay/ramp": { type: "list", value: "linear", list: RampTypes } as ParamList<
    typeof RampTypes
  >,
  "envelope/decay/value": { type: "float", value: 0.4, min: 0, max: 1, step: 0.01 },
  "envelope/decay/t": { type: "float", value: 0.3, min: 0.1, max: 10, step: 0.1 },
  "envelope/sustain/ramp": { type: "list", value: "linear", list: RampTypes } as ParamList<
    typeof RampTypes
  >,
  "envelope/sustain/value": { type: "float", value: 0.3, min: 0, max: 1, step: 0.01 },
  "envelope/sustain/t": { type: "float", value: 0.6, min: 0.1, max: 10, step: 0.1 },
  "envelope/release/ramp": { type: "list", value: "linear", list: RampTypes } as ParamList<
    typeof RampTypes
  >,
  "envelope/release/t": { type: "float", value: 0.6, min: 0.1, max: 10, step: 0.1 },
  "rng/seed": { type: "int", value: 12345, min: 1, max: 999_999_999 },
} satisfies ParamDict;
export type ShushSynthOpts = ParamDictProps<typeof ShushSynthParams>;

export function ShushDuration(o: ShushSynthOpts) {
  return o["envelope/attack/t"] + o["envelope/decay/t"] + o["envelope/sustain/t"] +
    o["envelope/release/t"];
}

export const Shush = (props: Partial<ShushSynthOpts> & SynthVoiceProps & { stop?: number }) => {
  const o = SynthOpts(ShushSynth, props);
  const duration = ShushDuration(o);
  const a = o["envelope/attack/t"];
  const d = a + o["envelope/decay/t"];
  const s = d + o["envelope/sustain/t"];
  const rng = useMemo(() => rngSeed(o["rng/seed"]), [o["rng/seed"]]);
  const v = props.velocity ?? 1;

  return (
    <gain
      gain={[
        { set: 0.001, at: 0 },
        Ramp(o["envelope/attack/ramp"], o["envelope/attack/value"] * v, a),
        Ramp(o["envelope/decay/ramp"], o["envelope/decay/value"] * v, d),
        Ramp(o["envelope/sustain/ramp"], o["envelope/sustain/value"] * v, s),
        Ramp(o["envelope/release/ramp"], 0.1, duration),
      ]}
    >
      <biquad
        type={o["lowpass/type"]}
        Q={o["lowpass/q"]}
        frequency={FilterSweep(
          o["lowpass/frequency"],
          o["lowpass/ramp"],
          o["lowpass/duration"],
        )}
      >
        <biquad
          type={o["filter/type"]}
          Q={o["filter/q"]}
          frequency={FilterSweep(
            o["filter/frequency"],
            o["filter/ramp"],
            o["filter/duration"],
          )}
        >
          <Noise duration={duration} rng={rng} stop={props.stop} onEnded={props.onEnded} />
        </biquad>
      </biquad>
    </gain>
  );
};

export const ShushSynth: SynthDef<typeof ShushSynthParams> = {
  label: "shush",
  params: ShushSynthParams,
  Component: Shush,
  duration: ShushDuration,
};
//#endregion

//#region shush loop
export interface ShushLoopProps {
  wind?: Partial<WindSynthOpts>;
  shush?: Partial<ShushSynthOpts>;
  at?: number[];
  length?: number;
}
export const ShushLoop = (props: ShushLoopProps) => (
  <>
    <Wind {...props.wind} />
    {(props.at ?? [1, 3]).map((t) => (
      <At key={t} t={t}>
        <Shush {...props.shush} stop={props.length ?? 1.5} />
      </At>
    ))}
  </>
);
//#endregion

//#region night vision
export const NightVisionSynthParams = {
  "sweep/down": { type: "float", value: 0.05, min: 0.001, max: 1, step: 0.001 },
  "sweep/up": { type: "float", value: 1.525, min: 0.01, max: 5, step: 0.01 },
  "freq/start": { type: "int", value: 5000, min: 20, max: 20000 },
  "freq/low": { type: "int", value: 1500, min: 20, max: 20000 },
  "freq/end": { type: "int", value: 2500, min: 20, max: 20000 },
  volume: { type: "float", value: 0.2, min: 0, max: 1, step: 0.01 },
  "wave/coeffs": { type: "string", value: "0 0.01 0.022 0.007 0.004 0.004" },
} satisfies ParamDict;
export type NightVisionSynthOpts = ParamDictProps<typeof NightVisionSynthParams>;

export function NightVisionWave(coeffs: string) {
  const real = ParseNumbers(coeffs);
  return { real, imag: real.map(() => 0) };
}

export const NightVision = (props: Partial<NightVisionSynthOpts> & SynthVoiceProps) => {
  const o = SynthOpts(NightVisionSynth, props);
  const down = o["sweep/down"];
  const end = down + o["sweep/up"];
  const wave = useMemo(() => NightVisionWave(o["wave/coeffs"]), [o["wave/coeffs"]]);
  return (
    <gain gain={[{ set: o.volume * (props.velocity ?? 1), at: 0 }, { linear: 0, at: end }]}>
      <osc
        wave={wave}
        frequency={[
          { set: o["freq/start"], at: 0 },
          { linear: o["freq/low"], at: down },
          { linear: o["freq/end"], at: end },
        ]}
        stop={end}
        onEnded={props.onEnded}
      />
    </gain>
  );
};

export const NightVisionSynth: SynthDef<typeof NightVisionSynthParams> = {
  label: "night vision",
  params: NightVisionSynthParams,
  Component: NightVision,
  duration: (o) => o["sweep/down"] + o["sweep/up"],
};
//#endregion

//#region noise tone
export const NoiseToneParams = {
  "noise/type": { type: "list", value: "pink", list: WbaxNoiseTypes } as ParamList<
    typeof WbaxNoiseTypes
  >,
  "noise/duration": { type: "float", value: 1, min: 0.1, max: 10, step: 0.1 },
  "playback/rampUp": { type: "float", value: 1, min: 0.1, max: 10, step: 0.1 },
  "playback/rampDown": { type: "float", value: 1, min: 0.1, max: 10, step: 0.1 },
  "rng/seed": { type: "int", value: 12345, min: 0, max: 999_999_999 },
} satisfies ParamDict;
export type NoiseToneOpts = ParamDictProps<typeof NoiseToneParams>;

export const NoiseTone = (props: Partial<NoiseToneOpts> & SynthVoiceProps) => {
  const o = SynthOpts(NoiseToneSynth, props);
  const seed = o["rng/seed"];
  const rng = useMemo(() => (seed > 0 ? rngSeed(seed) : Math.random), [seed]);
  const up = o["playback/rampUp"];
  return (
    <gain
      gain={[
        { set: 0, at: 0 },
        { linear: 0.125, at: up / 2 },
        { linear: 0.5 * (props.velocity ?? 1), at: up },
        { linear: 0, at: o["playback/rampDown"] },
      ]}
    >
      <Noise
        type={o["noise/type"] as WbaxNoiseType}
        duration={o["noise/duration"]}
        rng={rng}
        onEnded={props.onEnded}
      />
    </gain>
  );
};

export const NoiseToneSynth: SynthDef<typeof NoiseToneParams> = {
  label: "noise",
  params: NoiseToneParams,
  Component: NoiseTone,
  duration: (o) => o["noise/duration"],
};
//#endregion

//#region ocean
export const OceanSynthParams = {
  "ocean/duration": { type: "float", value: 1, min: 0.1, max: 30, step: 0.1 },
  "noise/type": { type: "list", value: "pink", list: WbaxNoiseTypes } as ParamList<
    typeof WbaxNoiseTypes
  >,
  "rng/seed": { type: "int", value: 12345, min: 1, max: 999_999_999 },
  "gain/start": { type: "float", value: 1, min: 0, max: 2, step: 0.01 },
  "highshelf/type": { type: "list", value: "highshelf", list: BiquadTypes } as ParamList<
    typeof BiquadTypes
  >,
  "highshelf/frequency": { type: "int", value: 15000, min: 0, max: 22050 },
  "highshelf/ramp": { type: "int", value: 15000, min: 0, max: 22050 },
  "highshelf/q": { type: "float", value: 0.7, min: 0.1, max: 10, step: 0.1 },
  "highshelf/duration": { type: "float", value: 1, min: 0.01, max: 10, step: 0.01 },
  "lowshelf/type": { type: "list", value: "lowshelf", list: BiquadTypes } as ParamList<
    typeof BiquadTypes
  >,
  "lowshelf/frequency": { type: "int", value: 220, min: 0, max: 1000 },
  "lowshelf/ramp": { type: "int", value: 220, min: 0, max: 1000 },
  "lowshelf/q": { type: "float", value: 0.7, min: 0.1, max: 10, step: 0.1 },
  "lowshelf/duration": { type: "float", value: 1, min: 0.01, max: 10, step: 0.01 },
  "highpass/type": { type: "list", value: "highpass", list: BiquadTypes } as ParamList<
    typeof BiquadTypes
  >,
  "highpass/frequency": { type: "int", value: 800, min: 0, max: 8000 },
  "highpass/ramp": { type: "int", value: 800, min: 0, max: 8000 },
  "highpass/q": { type: "float", value: 0.7, min: 0.1, max: 10, step: 0.1 },
  "highpass/duration": { type: "float", value: 1, min: 0.01, max: 10, step: 0.01 },
  "lowpass/type": { type: "list", value: "lowpass", list: BiquadTypes } as ParamList<
    typeof BiquadTypes
  >,
  "lowpass/frequency": { type: "int", value: 880, min: 0, max: 8000 },
  "lowpass/ramp": { type: "int", value: 880, min: 0, max: 8000 },
  "lowpass/q": { type: "float", value: 0.7, min: 0.1, max: 10, step: 0.1 },
  "lowpass/duration": { type: "float", value: 1, min: 0.01, max: 10, step: 0.01 },
} satisfies ParamDict;
export type OceanSynthOpts = ParamDictProps<typeof OceanSynthParams>;

const OceanStages = ["highshelf", "lowshelf", "highpass", "lowpass"] as const;

export const Ocean = (props: Partial<OceanSynthOpts> & SynthVoiceProps & { loop?: boolean }) => {
  const o = SynthOpts(OceanSynth, props);
  const duration = o["ocean/duration"];
  const rng = useMemo(() => rngSeed(o["rng/seed"]), [o["rng/seed"]]);
  const source = (
    <gain gain={o["gain/start"] * (props.velocity ?? 1)}>
      <Noise
        type={o["noise/type"] as WbaxNoiseType}
        duration={duration}
        rng={rng}
        loop={props.loop}
        stop={props.loop ? undefined : duration}
        onEnded={props.onEnded}
      />
    </gain>
  );
  return OceanStages.reduce((input, stage) => (
    <biquad
      type={o[`${stage}/type`]}
      Q={o[`${stage}/q`]}
      frequency={FilterSweep(o[`${stage}/frequency`], o[`${stage}/ramp`], o[`${stage}/duration`])}
    >
      {input}
    </biquad>
  ), source);
};

export const OceanSynth: SynthDef<typeof OceanSynthParams> = {
  label: "ocean",
  params: OceanSynthParams,
  Component: Ocean,
  duration: (o) => o["ocean/duration"],
};
//#endregion
